'use client'

import React, { createContext, useContext, ReactNode, useEffect, useRef, useCallback } from 'react'
import { usePersistStore } from '@/store/persist.store'
import { useChatStore } from '@/store/chat.store'
import { useSocketStore } from '@/store/socket.store'

type ScrollOptions = {
  behavior?: 'smooth' | 'instant'
  delay?: number
}

interface ChatContextType {
  scrollToBottom: (options?: ScrollOptions) => void
  scrollAreaRef: React.RefObject<HTMLDivElement>
  lastMessageRef: React.RefObject<HTMLDivElement>
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined)

const DEFAULT_TOKEN = 'guest'

function normalizeLanguage(lang: unknown) {
  if (typeof lang !== 'string') return null

  const normalizedLang = lang.trim().replace('_', '-').toLowerCase()
  if (!/^[a-z]{2,3}(-[a-z0-9]{2,8})*$/.test(normalizedLang)) return null

  return normalizedLang.split('-')[0]
}

function updateLanguageQuery(lang: string) {
  const url = new URL(window.location.href)

  if (url.searchParams.get('lang') === lang) return

  url.searchParams.set('lang', lang)
  window.location.replace(url.toString())
}

export default function ChatProvider({ children }: { children: ReactNode }) {
  const token = usePersistStore(state => state.token)
  const sessionId = usePersistStore(state => state.sessionId)
  const guestId = usePersistStore(state => state.guestId)
  const siteId = usePersistStore(state => state.siteId)
  const parentOrigin = useRef<string | null>(null)
  const setSessionId = usePersistStore(state => state.setSessionId)
  const setSiteId = usePersistStore(state => state.setSiteId)
  const setToken = usePersistStore(state => state.setToken)
  const setUser = usePersistStore(state => state.setUser)

  const setIsChatIframeShown = useChatStore(state => state.setIsChatIframeShown)
  const setComm100IntegrationId = useChatStore(state => state.setComm100IntegrationId)
  const setComm100SiteId = useChatStore(state => state.setComm100SiteId)
  const setComm100Url = useChatStore(state => state.setComm100Url)
  const setIsGuestIdActive = usePersistStore(state => state.setIsGuestIdActive)
  const setIsFirstButtonClick = useChatStore(state => state.setIsFirstButtonClick)
  const setIsIframeReady = useChatStore(state => state.setIsIframeReady)
  const setIsLsOnly = useChatStore(state => state.setIsLsOnly)
  const setParentWidth = useChatStore(state => state.setParentWidth)

  const isAuthSuccess = useSocketStore(state => state.isAuthSuccess)

  // Scroll functionality (kept in context because it uses refs)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const lastMessageRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(async (options: ScrollOptions = {}) => {
    const { behavior = 'smooth', delay = 50 } = options

    await new Promise(resolve => setTimeout(resolve, delay))

    if (lastMessageRef.current && scrollAreaRef.current) {
      const container = scrollAreaRef.current
      const target = lastMessageRef.current

      container.scrollTo({
        top: target.offsetTop + target.offsetHeight + 8 - container.clientHeight,
        behavior
      })
    } else if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({
        top: scrollAreaRef.current.scrollHeight + 8,
        behavior
      })
    }
  }, [])

  useEffect(() => {
    if (token === DEFAULT_TOKEN) {
      setIsGuestIdActive(true)
    }

    function handleContentWindowMessage(event: MessageEvent) {
      if (typeof event.data !== 'object' || !event.data.type) return

      const { type, payload } = event.data

      if (type === 'AUTH') {
        if (event.source !== window.parent) return
        parentOrigin.current = event.origin
        window.parent.postMessage(
          { type: 'PRESENCE_GUEST', payload: { siteId: payload?.siteId, guestId: usePersistStore.getState().guestId } },
          event.origin
        )

        if (token !== DEFAULT_TOKEN && !payload?.token) {
          // Logout from website
          setIsGuestIdActive(true)
          setSessionId(null)
          setUser(null)
        }

        setSiteId(payload?.siteId)
        setToken(payload?.token || DEFAULT_TOKEN)
        setIsLsOnly(payload?.ls === true)
      }

      if (
        type === 'PRESENCE_GUEST_REQUEST' &&
        event.source === window.parent &&
        event.origin === parentOrigin.current
      ) {
        window.parent.postMessage({ type: 'PRESENCE_GUEST', payload: { siteId, guestId } }, event.origin)
      }

      if (type === 'COMM100') {
        setComm100IntegrationId(payload?.comm100IntegrationId || null)
        setComm100SiteId(payload?.comm100SiteId || null)
        setComm100Url(payload?.comm100Url || null)
      }

      if (type === 'IS_OPEN') {
        setIsChatIframeShown(payload?.isChatIframeShown || false)
      }

      if (type === 'FIRST_BUTTON_CLICK') {
        setIsFirstButtonClick(payload?.isFirstButtonClick || false)
      }

      if (type === 'PARENT_WIDTH') {
        setParentWidth(payload?.width || null)
      }

      if (type === 'LANGUAGE_CHANGE') {
        const lang = normalizeLanguage(payload?.lang)
        if (lang) {
          updateLanguageQuery(lang)
        }
      }
    }

    window.addEventListener('message', handleContentWindowMessage)

    return () => window.removeEventListener('message', handleContentWindowMessage)
  }, [
    token,
    siteId,
    guestId,
    setSessionId,
    setUser,
    setSiteId,
    setToken,
    setIsChatIframeShown,
    setIsFirstButtonClick,
    setComm100IntegrationId,
    setComm100SiteId,
    setComm100Url,
    setIsLsOnly,
    setParentWidth,
    setIsGuestIdActive
  ])

  useEffect(() => {
    if (!parentOrigin.current || !siteId) return
    window.parent.postMessage({ type: 'PRESENCE_GUEST', payload: { siteId, guestId } }, parentOrigin.current)
  }, [siteId, guestId])

  useEffect(() => {
    window.parent.postMessage(
      {
        type: 'IS_AUTH_SUCCESS',
        payload: {
          isAuthSuccess: isAuthSuccess
        }
      },
      '*'
    )
  }, [isAuthSuccess])

  useEffect(() => {
    setIsIframeReady(true)
    window.parent.postMessage({ type: 'IFRAME_READY', payload: { isIframeReady: true } }, '*')
  }, [setIsIframeReady])

  useEffect(() => {
    window.parent.postMessage({ type: 'SESSION_ID', payload: { sessionId: sessionId ?? null } }, '*')
  }, [sessionId])

  useEffect(() => {
    window.parent.postMessage({ type: 'GET_PARENT_WIDTH' }, '*')

    const interval = setInterval(() => {
      window.parent.postMessage({ type: 'GET_PARENT_WIDTH' }, '*')
    }, 200)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <ChatContext.Provider
      value={{
        scrollToBottom,
        scrollAreaRef,
        lastMessageRef
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export function useChatContext() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider')
  }

  return context
}
